// frontend/src/pages/SendMoney.jsx
// PROFESSIONAL VERSION - Wise-like Multi-Step UI
// Complete with Crypto, Bank (Plaid), and Card (Stripe) support
// UPDATED: 100+ Currencies Support - FIXED BLANK PAGE BUG

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { usePlaidLink } from 'react-plaid-link';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import SignupModal from '../SignupModal';
import LoginModal from '../LoginModal';
import ActivateCardModal from '../ActivateCardModal';
import {
  Bitcoin, Building, CreditCard, DollarSign, Send, AlertCircle,
  Check, X, ChevronRight, Loader, Shield, Clock, Globe, Info,
  CheckCircle, XCircle, ArrowRight, ArrowLeft, User, MapPin,
  ShoppingCart, Menu, ChevronDown
} from 'lucide-react';

// Flag Icon Component - Works on ALL devices including Windows
const FlagIcon = ({ countryCode, size = "w-6 h-5" }) => {
  if (!countryCode) return null;
  const code = countryCode.toLowerCase();
  return (
    <img 
      src={`https://flagcdn.com/${code}.svg`}
      alt={countryCode}
      className={`${size} rounded shadow-sm inline-block`}
      onError={(e) => {
        e.target.style.display = 'none';
      }}
    />
  );
};

// Currency to Country Code Mapping
const CURRENCY_TO_COUNTRY = {
  'USD': 'us', 'EUR': 'eu', 'GBP': 'gb', 'NGN': 'ng', 'CAD': 'ca',
  'AUD': 'au', 'JPY': 'jp', 'CHF': 'ch', 'KES': 'ke', 'GHS': 'gh',
  'ZAR': 'za', 'EGP': 'eg', 'MAD': 'ma', 'TND': 'tn', 'DZD': 'dz',
  'UGX': 'ug', 'TZS': 'tz', 'RWF': 'rw', 'ETB': 'et', 'ZMW': 'zm',
  'BWP': 'bw', 'MWK': 'mw', 'MUR': 'mu', 'AOA': 'ao', 'MZN': 'mz',
  'NAD': 'na', 'XOF': 'ci', 'XAF': 'cm', 'CNY': 'cn', 'INR': 'in',
  'HKD': 'hk', 'SGD': 'sg', 'KRW': 'kr', 'TWD': 'tw', 'PKR': 'pk',
  'BDT': 'bd', 'LKR': 'lk', 'NPR': 'np', 'PHP': 'ph', 'IDR': 'id',
  'MYR': 'my', 'THB': 'th', 'VND': 'vn', 'KHR': 'kh', 'LAK': 'la',
  'MMK': 'mm', 'BND': 'bn', 'NZD': 'nz', 'AED': 'ae', 'SAR': 'sa',
  'QAR': 'qa', 'KWD': 'kw', 'BHD': 'bh', 'OMR': 'om', 'JOD': 'jo',
  'ILS': 'il', 'TRY': 'tr', 'LBP': 'lb', 'IQD': 'iq', 'NOK': 'no',
  'SEK': 'se', 'DKK': 'dk', 'PLN': 'pl', 'CZK': 'cz', 'HUF': 'hu',
  'RON': 'ro', 'BGN': 'bg', 'HRK': 'hr', 'RSD': 'rs', 'RUB': 'ru',
  'UAH': 'ua', 'ISK': 'is', 'BRL': 'br', 'MXN': 'mx', 'ARS': 'ar',
  'CLP': 'cl', 'COP': 'co', 'PEN': 'pe', 'VES': 've', 'UYU': 'uy',
  'BOB': 'bo', 'PYG': 'py', 'GTQ': 'gt', 'HNL': 'hn', 'NIO': 'ni',
  'CRC': 'cr', 'PAB': 'pa', 'DOP': 'do', 'JMD': 'jm', 'TTD': 'tt',
  'BBD': 'bb', 'HTG': 'ht'
};

// ==========================================
// 100+ SUPPORTED CURRENCIES - THIS WAS MISSING!
// ==========================================
const SUPPORTED_CURRENCIES = [
  // Popular/Most Used
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CHF', name: 'Swiss Franc' },
  
  // Africa
  { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'GHS', name: 'Ghanaian Cedi' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'MAD', name: 'Moroccan Dirham' },
  { code: 'TND', name: 'Tunisian Dinar' },
  { code: 'DZD', name: 'Algerian Dinar' },
  { code: 'UGX', name: 'Ugandan Shilling' },
  { code: 'TZS', name: 'Tanzanian Shilling' },
  { code: 'RWF', name: 'Rwandan Franc' },
  { code: 'ETB', name: 'Ethiopian Birr' },
  { code: 'ZMW', name: 'Zambian Kwacha' },
  { code: 'BWP', name: 'Botswana Pula' },
  { code: 'MWK', name: 'Malawian Kwacha' },
  { code: 'MUR', name: 'Mauritian Rupee' },
  { code: 'AOA', name: 'Angolan Kwanza' },
  { code: 'MZN', name: 'Mozambican Metical' },
  { code: 'NAD', name: 'Namibian Dollar' },
  { code: 'XOF', name: 'West African CFA' },
  { code: 'XAF', name: 'Central African CFA' },
  
  // Asia
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'TWD', name: 'Taiwan Dollar' },
  { code: 'PKR', name: 'Pakistani Rupee' },
  { code: 'BDT', name: 'Bangladeshi Taka' },
  { code: 'LKR', name: 'Sri Lankan Rupee' },
  { code: 'NPR', name: 'Nepalese Rupee' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'VND', name: 'Vietnamese Dong' },
  { code: 'KHR', name: 'Cambodian Riel' },
  { code: 'LAK', name: 'Lao Kip' },
  { code: 'MMK', name: 'Myanmar Kyat' },
  { code: 'BND', name: 'Brunei Dollar' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  
  // Middle East
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'QAR', name: 'Qatari Riyal' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'JOD', name: 'Jordanian Dinar' },
  { code: 'ILS', name: 'Israeli Shekel' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'LBP', name: 'Lebanese Pound' },
  { code: 'IQD', name: 'Iraqi Dinar' },
  
  // Europe
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'PLN', name: 'Polish Złoty' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'BGN', name: 'Bulgarian Lev' },
  { code: 'HRK', name: 'Croatian Kuna' },
  { code: 'RSD', name: 'Serbian Dinar' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'UAH', name: 'Ukrainian Hryvnia' },
  { code: 'ISK', name: 'Icelandic Króna' },
  
  // Americas
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'PEN', name: 'Peruvian Sol' },
  { code: 'VES', name: 'Venezuelan Bolívar' },
  { code: 'UYU', name: 'Uruguayan Peso' },
  { code: 'BOB', name: 'Bolivian Boliviano' },
  { code: 'PYG', name: 'Paraguayan Guaraní' },
  { code: 'GTQ', name: 'Guatemalan Quetzal' },
  { code: 'HNL', name: 'Honduran Lempira' },
  { code: 'NIO', name: 'Nicaraguan Córdoba' },
  { code: 'CRC', name: 'Costa Rican Colón' },
  { code: 'PAB', name: 'Panamanian Balboa' },
  { code: 'DOP', name: 'Dominican Peso' },
  { code: 'JMD', name: 'Jamaican Dollar' },
  { code: 'TTD', name: 'Trinidad Dollar' },
  { code: 'BBD', name: 'Barbadian Dollar' },
  { code: 'HTG', name: 'Haitian Gourde' },
];

const menuItems = {
  Services: [
    { name:'Send Money', path:'/send-money' },
    { name:'Crypto Cards', path:'/crypto-cards' },
    { name:'Digital Wallets', path:'/digital-wallets' },
    { name:'Business Solutions', path:'/business-solutions' },
  ],
  Company: [
    { name:'About Us', path:'/about-us' },
    { name:'Careers', path:'/careers' },
    { name:'Press', path:'/press' },
    { name:'Investors', path:'/investors' },
  ],
  Support: [
    { name:'Help Center', path:'/help-center' },
    { name:'Contact Us', path:'/contact-us' },
    { name:'API Documentation', path:'/api-documentation' },
    { name:'Security', path:'/security' },
  ],
  Legal: [
    { name:'Terms of Service', path:'/terms-of-service' },
    { name:'Privacy Policy', path:'/privacy-policy' },
    { name:'Compliance', path:'/compliance' },
    { name:'Licenses', path:'/licenses' },
  ],
};

const SendMoney = ({ user }) => {
  // ==========================================
  // STRIPE INTEGRATION
  // ==========================================
  const stripe = useStripe();
  const elements = useElements();

  // ==========================================
  // STATE MANAGEMENT
  // ==========================================
  // Step control
  const [currentStep, setCurrentStep] = useState(1);
  const STEPS = {
    AMOUNT: 1,
    PAYMENT: 2,
    RECIPIENT: 3,
    REVIEW: 4
  };

  // Modal states
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showActivateCardModal, setShowActivateCardModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [loginStep, setLoginStep] = useState('email');
  const [signupData, setSignupData] = useState({ firstName:'', lastName:'', email:'', phone:'', country:'US', password:'', confirmPassword:'', code:'', generatedCode:'' });
  const [loginData, setLoginData] = useState({ email:'', phone:'', loginMethod:'email', code:'', generatedCode:'' });
  const [activateData, setActivateData] = useState({ cardNumber:'', pin:'', code:'', generatedCode:'' });
  const toggleSubmenu = (menu) => setExpandedMenu(expandedMenu === menu ? null : menu);

  const signupCountries = [
    { code:'US', name:'United States', flag:'🇺🇸', requiresSSN:true },
    { code:'GB', name:'United Kingdom', flag:'🇬🇧', requiresSSN:false },
    { code:'NG', name:'Nigeria', flag:'🇳🇬', requiresSSN:false },
    { code:'GH', name:'Ghana', flag:'🇬🇭', requiresSSN:false },
    { code:'CA', name:'Canada', flag:'🇨🇦', requiresSSN:false },
    { code:'AU', name:'Australia', flag:'🇦🇺', requiresSSN:false },
  ];

  // Data states
  const [wallets, setWallets] = useState([]);
  const [cards, setCards] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [banks, setBanks] = useState([]);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

  // Form states
  const [amount, setAmount] = useState('');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [recipientCountry, setRecipientCountry] = useState('NG');
  const [toCurrency, setToCurrency] = useState('');
  
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('crypto');
  const [selectedWallet, setSelectedWallet] = useState('');
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  
  const [recipientBankCode, setRecipientBankCode] = useState('');
  const [recipientAccountNumber, setRecipientAccountNumber] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientVerified, setRecipientVerified] = useState(false);
  const [verifyingRecipient, setVerifyingRecipient] = useState(false);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingBanks, setLoadingBanks] = useState(false);
  
  // Quote state
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  
  // Plaid state
  const [linkToken, setLinkToken] = useState(null);
  const [plaidLoading, setPlaidLoading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://globalpay-backend-v2.onrender.com';

  // ==========================================
  // LOAD DATA
  // ==========================================
  useEffect(() => {
    if (user?.id) {
      loadWalletsAndCards();
      loadBankAccounts();
      loadCountries();
    }
  }, [user]);

  useEffect(() => {
    if (recipientCountry) {
      loadBanksForCountry(recipientCountry);
      const country = countries.find(c => c.code === recipientCountry);
      if (country) {
        setToCurrency(country.currency);
      }
    }
  }, [recipientCountry, countries]);

  // Auto-calculate quote when amount/currency changes
  useEffect(() => {
    if (amount && parseFloat(amount) > 0 && fromCurrency && toCurrency) {
      const timer = setTimeout(() => {
        calculateQuote();
      }, 500); // Debounce
      return () => clearTimeout(timer);
    }
  }, [amount, fromCurrency, toCurrency]);

  const loadWalletsAndCards = async () => {
    try {
      // Load wallets
      const { data: walletsData, error: walletsError } = await supabase
        .from('wallets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });
      
      if (!walletsError) {
        setWallets(walletsData || []);
        if (walletsData && walletsData.length > 0) {
          setSelectedWallet(walletsData[0].id);
        }
      }

      // Load cards
      const { data: cardsData, error: cardsError } = await supabase
        .from('crypto_cards')
        .select('*')
        .eq('activated_by', user.id)
        .eq('status', 'activated')
        .order('activated_at', { ascending: false});
      
      if (!cardsError) {
        setCards(cardsData || []);
      }
      
    } catch (error) {
      console.error('Load error:', error);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_BASE_URL}/payments/bank-accounts`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBankAccounts(data.data?.bankAccounts || []);
      }
    } catch (error) {
      console.error('Error loading bank accounts:', error);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/banks/countries`);
      if (response.ok) {
        const data = await response.json();
        setCountries(data.countries || []);
      }
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const loadBanksForCountry = async (countryCode) => {
    setLoadingBanks(true);
    setBanks([]);
    setRecipientBankCode('');
    setRecipientVerified(false);
    setRecipientName('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/banks/${countryCode}`);
      if (response.ok) {
        const data = await response.json();
        setBanks(data.banks || []);
      }
    } catch (error) {
      console.error('Error loading banks:', error);
    } finally {
      setLoadingBanks(false);
    }
  };

  // ==========================================
  // QUOTE CALCULATION
  // ==========================================
  const calculateQuote = async () => {
    if (!amount || parseFloat(amount) <= 0) return;

    setLoadingQuote(true);
    setError('');

    try {
      // Get session but don't require it for quotes - guests should be able to see rates
      const { data: { session } } = await supabase.auth.getSession();
      
      // Build headers - include auth token only if available
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add authorization if user is logged in
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(`${API_BASE_URL}/payments/calculate-conversion`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          amount: parseFloat(amount),
          fromCurrency,
          toCurrency,
          transferType: recipientCountry === 'US' ? 'domestic' : 'international'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to calculate quote');
      }

      const data = await response.json();
      setQuote(data.data);
    } catch (error) {
      console.error('Quote error:', error);
      setError('Failed to calculate quote. Please try again.');
    } finally {
      setLoadingQuote(false);
    }
  };

  // ==========================================
  // PLAID INTEGRATION
  // ==========================================
  const createLinkToken = async () => {
    setPlaidLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE_URL}/payments/plaid/create-link-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to create link token');

      const data = await response.json();
      setLinkToken(data.data.linkToken);
    } catch (error) {
      console.error('Plaid error:', error);
      setError('Failed to initialize bank connection');
    } finally {
      setPlaidLoading(false);
    }
  };

  const onPlaidSuccess = async (publicToken) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE_URL}/payments/plaid/exchange-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ publicToken })
      });

      if (!response.ok) throw new Error('Failed to link bank account');

      setSuccess('Bank account linked successfully!');
      loadBankAccounts();
    } catch (error) {
      console.error('Exchange token error:', error);
      setError('Failed to link bank account');
    }
  };

  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
  });

  // ==========================================
  // RECIPIENT VERIFICATION
  // ==========================================
  const verifyRecipient = async () => {
    if (!recipientBankCode || !recipientAccountNumber) {
      setError('Please select bank and enter account number');
      return;
    }

    setVerifyingRecipient(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE_URL}/banks/verify-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          bankCode: recipientBankCode,
          accountNumber: recipientAccountNumber,
          country: recipientCountry
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Verification failed');
      }

      const data = await response.json();
      setRecipientName(data.data.accountName);
      setRecipientVerified(true);
      setSuccess('Recipient verified successfully!');
    } catch (error) {
      console.error('Verification error:', error);
      setError(error.message || 'Failed to verify recipient');
      setRecipientVerified(false);
      setRecipientName('');
    } finally {
      setVerifyingRecipient(false);
    }
  };

  // ==========================================
  // SEND MONEY HANDLER
  // ==========================================
  const handleSendMoney = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      let endpoint = '';
      let body = {};

      // Prepare request based on payment method
      if (selectedPaymentMethod === 'crypto') {
        endpoint = `${API_BASE_URL}/payments/send/crypto`;
        const wallet = wallets.find(w => w.id === selectedWallet) || 
                      cards.find(c => c.id === selectedWallet);
        
        body = {
          amount: parseFloat(amount),
          fromCurrency,
          toCurrency,
          walletId: wallet?.id,
          cardId: wallet?.card_id || wallet?.id,
          recipient: {
            bankCode: recipientBankCode,
            accountNumber: recipientAccountNumber,
            accountName: recipientName,
            country: recipientCountry
          },
          transferType: recipientCountry === 'US' ? 'domestic' : 'international'
        };
      } else if (selectedPaymentMethod === 'bank') {
        endpoint = `${API_BASE_URL}/payments/send/bank`;
        body = {
          amount: parseFloat(amount),
          fromCurrency,
          toCurrency,
          bankAccountId: selectedBankAccount,
          recipient: {
            bankCode: recipientBankCode,
            accountNumber: recipientAccountNumber,
            accountName: recipientName,
            country: recipientCountry
          },
          transferType: recipientCountry === 'US' ? 'domestic' : 'international'
        };
      } else if (selectedPaymentMethod === 'card') {
        // Process Stripe payment first
        if (!stripe || !elements) {
          throw new Error('Stripe not loaded');
        }

        const cardElement = elements.getElement(CardElement);
        const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });

        if (stripeError) throw new Error(stripeError.message);

        endpoint = `${API_BASE_URL}/payments/send/card`;
        body = {
          amount: parseFloat(amount),
          fromCurrency,
          toCurrency,
          paymentMethodId: paymentMethod.id,
          recipient: {
            bankCode: recipientBankCode,
            accountNumber: recipientAccountNumber,
            accountName: recipientName,
            country: recipientCountry
          },
          transferType: recipientCountry === 'US' ? 'domestic' : 'international'
        };
      }

      // Send payment request
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Payment failed');
      }

      const data = await response.json();
      setSuccess(`Money sent successfully! Transaction ID: ${data.data.transaction.id}`);
      
      // Reset form
      setTimeout(() => {
        setCurrentStep(1);
        setAmount('');
        setRecipientAccountNumber('');
        setRecipientName('');
        setRecipientVerified(false);
        setQuote(null);
      }, 3000);

    } catch (error) {
      console.error('Send error:', error);
      setError(error.message || 'Failed to send money');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // VALIDATION HELPERS
  // ==========================================
  const canProceedFromStep1 = () => {
    return amount && parseFloat(amount) > 0 && toCurrency && quote;
  };

  const canProceedFromStep2 = () => {
    if (selectedPaymentMethod === 'crypto') {
      return selectedWallet;
    } else if (selectedPaymentMethod === 'bank') {
      return selectedBankAccount && bankAccounts.length > 0;
    } else if (selectedPaymentMethod === 'card') {
      return stripe && elements;
    }
    return false;
  };

  const canProceedFromStep3 = () => {
    return recipientVerified && recipientName && recipientBankCode && recipientAccountNumber;
  };

  const getSelectedWalletDetails = () => {
    return wallets.find(w => w.id === selectedWallet) || 
           cards.find(c => c.id === selectedWallet);
  };

  const getSelectedBankDetails = () => {
    return bankAccounts.find(b => b.id === selectedBankAccount);
  };

  // ==========================================

  // ==========================================
  // RENDER: PROGRESS STEPS
  // ==========================================
  const renderProgressSteps = () => (
    <div className="flex items-center justify-center mb-8 px-2">
      {[
        { number: 1, label: 'Amount',    icon: DollarSign },
        { number: 2, label: 'Payment',   icon: CreditCard },
        { number: 3, label: 'Recipient', icon: User },
        { number: 4, label: 'Review',    icon: CheckCircle }
      ].map((step, index) => (
        <React.Fragment key={step.number}>
          <div className="flex flex-col items-center">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all ${
              currentStep > step.number
                ? 'bg-emerald-500 border-emerald-500 text-white'
                : currentStep === step.number
                  ? 'bg-violet-600 border-violet-600 text-white shadow-lg shadow-violet-200'
                  : 'bg-white border-slate-200 text-slate-400'
            }`}>
              {currentStep > step.number ? (
                <Check className="w-5 h-5" />
              ) : (
                <step.icon className="w-5 h-5" />
              )}
            </div>
            <span className={`text-xs font-semibold mt-1.5 ${
              currentStep >= step.number ? 'text-slate-700' : 'text-slate-400'
            }`}>
              {step.label}
            </span>
          </div>
          {index < 3 && (
            <div className={`h-0.5 w-14 mx-1 mb-5 rounded-full ${
              currentStep > step.number ? 'bg-emerald-400' : 'bg-slate-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // ==========================================
  // RENDER: STEP 1 - AMOUNT
  // ==========================================
  const renderStep1 = () => (
    <div className="space-y-5">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-900 mb-1">How much are you sending?</h2>
        <p className="text-slate-500 text-sm">Live exchange rates for 100+ currencies</p>
      </div>

      {/* You send box */}
      <div className="bg-white rounded-2xl border-2 border-slate-200 focus-within:border-violet-400 transition-colors shadow-sm">
        <div className="px-5 pt-4 pb-0.5">
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest">You send</label>
        </div>
        <div className="flex items-center px-5 pb-4 gap-3">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            step="0.01"
            min="1"
            className="flex-1 text-4xl font-bold border-none outline-none text-slate-900 placeholder-slate-200 bg-transparent min-w-0"
          />
          {/* Currency selector with flag image overlay — no emoji */}
          <div className="flex-shrink-0 relative">
            <select
              value={fromCurrency}
              onChange={(e) => setFromCurrency(e.target.value)}
              className="appearance-none bg-violet-600 text-white font-bold rounded-xl pl-9 pr-7 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 cursor-pointer w-28"
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
              <FlagIcon countryCode={CURRENCY_TO_COUNTRY[fromCurrency]} size="w-5 h-4" />
            </div>
            <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-violet-200 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Arrow connector */}
      <div className="flex justify-center my-1">
        <div className="w-8 h-8 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center shadow-sm">
          <ArrowRight className="w-4 h-4 text-slate-400" />
        </div>
      </div>

      {/* Recipient gets — custom dropdown */}
      <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-5">
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
          Recipient gets
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
            className="w-full flex items-center gap-3 border-2 border-slate-200 hover:border-violet-400 focus:border-violet-500 focus:outline-none transition-colors rounded-xl px-4 py-3 bg-white text-left"
          >
            {toCurrency ? (
              <>
                <FlagIcon countryCode={CURRENCY_TO_COUNTRY[toCurrency]} size="w-7 h-5" />
                <span className="flex-1 font-bold text-slate-900 text-lg">{toCurrency}</span>
                <span className="text-xs text-slate-400 hidden sm:block">
                  {SUPPORTED_CURRENCIES.find(c => c.code === toCurrency)?.name}
                </span>
              </>
            ) : (
              <span className="flex-1 text-slate-400 text-sm">Select recipient currency (100+ available)</span>
            )}
            <svg
              className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showCurrencyDropdown && (
            <div className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
              <div className="p-3 border-b border-slate-100">
                <input
                  autoFocus
                  placeholder="Search currency..."
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-400 placeholder-slate-400"
                  onChange={(e) => {
                    const val = e.target.value.toLowerCase();
                    e.target.dataset.search = val;
                    document.querySelectorAll('[data-currency-item]').forEach(item => {
                      const code = item.dataset.code.toLowerCase();
                      const name = item.dataset.name.toLowerCase();
                      item.style.display = (code.includes(val) || name.includes(val)) ? '' : 'none';
                    });
                  }}
                />
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-slate-50">
                {SUPPORTED_CURRENCIES.map((currency) => (
                  <button
                    key={currency.code}
                    type="button"
                    data-currency-item="true"
                    data-code={currency.code}
                    data-name={currency.name}
                    onClick={() => {
                      setToCurrency(currency.code);
                      setShowCurrencyDropdown(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-violet-50 text-left transition-colors ${toCurrency === currency.code ? 'bg-violet-50' : ''}`}
                  >
                    <FlagIcon countryCode={CURRENCY_TO_COUNTRY[currency.code]} size="w-7 h-5" />
                    <div>
                      <div className="font-bold text-slate-800 text-sm">{currency.code}</div>
                      <div className="text-slate-400 text-xs">{currency.name}</div>
                    </div>
                    {toCurrency === currency.code && <Check className="w-4 h-4 text-violet-600 ml-auto" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {loadingQuote && (
        <div className="flex items-center justify-center gap-2 py-3 text-slate-500 text-sm">
          <Loader className="w-4 h-4 animate-spin text-violet-500" />
          Calculating best rate...
        </div>
      )}

      {quote && !loadingQuote && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-emerald-100">
            <span className="text-slate-500 text-sm font-medium">Recipient gets</span>
            <span className="text-2xl font-extrabold text-emerald-700">
              {quote.convertedAmount?.toLocaleString()} <span className="text-lg font-bold">{toCurrency}</span>
            </span>
          </div>
          <div className="px-5 py-3 space-y-2 text-sm">
            <div className="flex justify-between text-slate-500">
              <span>Exchange rate</span>
              <span className="font-semibold text-slate-700">1 {fromCurrency} = {quote.exchangeRate} {toCurrency}</span>
            </div>
            <div className="flex justify-between text-slate-500">
              <span>Fee</span>
              <span className="font-semibold text-slate-700">${quote.fee}</span>
            </div>
            <div className="flex justify-between font-bold text-slate-900 pt-2 border-t border-emerald-200">
              <span>Total cost</span>
              <span>${quote.totalCost}</span>
            </div>
          </div>
          <div className="px-5 py-2.5 bg-emerald-100/60 flex items-center gap-2 text-xs text-emerald-700 font-semibold">
            <Clock className="w-3.5 h-3.5" />
            Estimated arrival: 5-30 minutes
          </div>
        </div>
      )}

      <button
        onClick={() => setCurrentStep(2)}
        disabled={!canProceedFromStep1()}
        className="w-full bg-violet-600 hover:bg-violet-700 text-white py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-lg shadow-violet-200/60"
      >
        Continue
        <ArrowRight className="w-5 h-5" />
      </button>

      <div className="flex items-center justify-center gap-5 text-xs text-slate-400 flex-wrap">
        <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Bank-level security</span>
        <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 5-30 min delivery</span>
        <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> 0% transparent fee</span>
      </div>
    </div>
  );

  // ==========================================
  // RENDER: STEP 2 - PAYMENT METHOD
  // ==========================================
  const renderStep2 = () => (
    <div className="space-y-5">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-900 mb-1">Choose payment method</h2>
        <p className="text-slate-500 text-sm">How would you like to pay?</p>
      </div>

      <div className="space-y-3">
        {/* Crypto Option */}
        <button
          onClick={() => setSelectedPaymentMethod('crypto')}
          className={`w-full p-4 rounded-2xl border-2 transition-all text-left flex items-center gap-4 ${
            selectedPaymentMethod === 'crypto'
              ? 'border-orange-400 bg-white shadow-sm'
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            selectedPaymentMethod === 'crypto' ? 'bg-orange-100' : 'bg-slate-100'
          }`}>
            <Bitcoin className={`w-6 h-6 ${selectedPaymentMethod === 'crypto' ? 'text-orange-500' : 'text-slate-400'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-800 text-sm">Crypto Wallet / Card</div>
            <div className="text-xs text-slate-500 mt-0.5">Pay with your Bitcoin wallet or card</div>
          </div>
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            selectedPaymentMethod === 'crypto' ? 'border-orange-400' : 'border-slate-300'
          }`}>
            {selectedPaymentMethod === 'crypto' && <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
          </div>
        </button>

        {selectedPaymentMethod === 'crypto' && (
          <div className="ml-4 space-y-2">
            {[...wallets, ...cards].map((item) => (
              <button
                key={item.id}
                onClick={() => setSelectedWallet(item.id)}
                className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all text-left ${
                  selectedWallet === item.id
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-slate-200 bg-white hover:border-orange-200'
                }`}
              >
                <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bitcoin className="w-5 h-5 text-orange-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 text-sm truncate">
                    {item.name || `Card ${item.card_number?.slice(-4) || 'XXXX'}`}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {item.btc_balance || item.current_btc_balance} BTC available
                  </div>
                </div>
                {selectedWallet === item.id && <Check className="w-5 h-5 text-orange-500 flex-shrink-0" />}
              </button>
            ))}
          </div>
        )}

        {/* Bank Option */}
        <button
          onClick={() => {
            setSelectedPaymentMethod('bank');
            if (!linkToken) createLinkToken();
          }}
          className={`w-full p-4 rounded-2xl border-2 transition-all text-left flex items-center gap-4 ${
            selectedPaymentMethod === 'bank'
              ? 'border-blue-500 bg-white shadow-sm'
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            selectedPaymentMethod === 'bank' ? 'bg-blue-100' : 'bg-slate-100'
          }`}>
            <Building className={`w-6 h-6 ${selectedPaymentMethod === 'bank' ? 'text-blue-600' : 'text-slate-400'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-800 text-sm">Bank Account (ACH)</div>
            <div className="text-xs text-slate-500 mt-0.5">Direct debit from your bank</div>
          </div>
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            selectedPaymentMethod === 'bank' ? 'border-blue-500' : 'border-slate-300'
          }`}>
            {selectedPaymentMethod === 'bank' && <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
          </div>
        </button>

        {selectedPaymentMethod === 'bank' && (
          <div className="ml-4 space-y-2">
            {bankAccounts.length === 0 ? (
              <button
                onClick={() => plaidReady && openPlaid()}
                disabled={!plaidReady || plaidLoading}
                className="w-full p-4 border-2 border-dashed border-blue-400 rounded-xl hover:border-blue-500 text-blue-600 flex items-center justify-center gap-2 text-sm font-semibold hover:bg-blue-50 transition-colors disabled:opacity-50"
              >
                {plaidLoading ? (
                  <><Loader className="w-4 h-4 animate-spin" />Loading...</>
                ) : (
                  <><Building className="w-4 h-4" />Connect Bank Account</>
                )}
              </button>
            ) : (
              <>
                {bankAccounts.map((account) => (
                  <button
                    key={account.id}
                    onClick={() => setSelectedBankAccount(account.id)}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all text-left ${
                      selectedBankAccount === account.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-blue-200'
                    }`}
                  >
                    <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Building className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-800 text-sm">{account.bank_name}</div>
                      <div className="text-xs text-slate-500">Account ****{account.account_number_last4}</div>
                    </div>
                    {selectedBankAccount === account.id && <Check className="w-5 h-5 text-blue-500 flex-shrink-0" />}
                  </button>
                ))}
                <button
                  onClick={() => plaidReady && openPlaid()}
                  disabled={!plaidReady}
                  className="w-full py-2.5 border border-dashed border-slate-300 rounded-xl text-xs text-slate-500 hover:text-blue-600 hover:border-blue-300 font-semibold transition-colors"
                >
                  + Add Another Bank
                </button>
              </>
            )}
          </div>
        )}

        {/* Card Option */}
        <button
          onClick={() => setSelectedPaymentMethod('card')}
          className={`w-full p-4 rounded-2xl border-2 transition-all text-left flex items-center gap-4 ${
            selectedPaymentMethod === 'card'
              ? 'border-violet-500 bg-white shadow-sm'
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
        >
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            selectedPaymentMethod === 'card' ? 'bg-violet-100' : 'bg-slate-100'
          }`}>
            <CreditCard className={`w-6 h-6 ${selectedPaymentMethod === 'card' ? 'text-violet-600' : 'text-slate-400'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-800 text-sm">Debit / Credit Card</div>
            <div className="text-xs text-slate-500 mt-0.5">Pay with any card via Stripe</div>
          </div>
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            selectedPaymentMethod === 'card' ? 'border-violet-500' : 'border-slate-300'
          }`}>
            {selectedPaymentMethod === 'card' && <div className="w-2.5 h-2.5 rounded-full bg-violet-600" />}
          </div>
        </button>

        {selectedPaymentMethod === 'card' && (
          <div className="ml-4">
            <div className="bg-white rounded-2xl border-2 border-violet-200 p-5 shadow-sm">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#0f172a',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      '::placeholder': { color: '#94a3b8' },
                    },
                    invalid: { color: '#ef4444' },
                    complete: { color: '#059669' },
                  },
                }}
              />
            </div>
            <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5 pl-1">
              <Shield className="w-3.5 h-3.5 text-emerald-500" />
              Secured by Stripe — card details never stored on our servers
            </p>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={() => setCurrentStep(1)}
          className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-4 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={() => setCurrentStep(3)}
          disabled={!canProceedFromStep2()}
          className="flex-1 bg-violet-600 hover:bg-violet-700 text-white py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-lg shadow-violet-200/60"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // ==========================================
  // RENDER: STEP 3 - RECIPIENT
  // ==========================================
  const renderStep3 = () => (
    <div className="space-y-5">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-slate-900 mb-1">Who are you sending to?</h2>
        <p className="text-slate-500 text-sm">Enter recipient bank details</p>
      </div>

      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">
          <MapPin className="w-3 h-3 inline mr-1" />
          Recipient Country
        </label>
        <select
          value={recipientCountry}
          onChange={(e) => setRecipientCountry(e.target.value)}
          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
        >
          {countries.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">
          <Building className="w-3 h-3 inline mr-1" />
          Recipient Bank
        </label>
        <select
          value={recipientBankCode}
          onChange={(e) => {
            setRecipientBankCode(e.target.value);
            setRecipientVerified(false);
            setRecipientName('');
          }}
          disabled={loadingBanks || banks.length === 0}
          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm disabled:opacity-50"
        >
          <option value="">
            {loadingBanks ? 'Loading banks...' : 'Select bank'}
          </option>
          {banks.map((bank) => (
            <option key={bank.code} value={bank.code}>
              {bank.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">
          Account Number
        </label>
        <input
          type="text"
          value={recipientAccountNumber}
          onChange={(e) => {
            setRecipientAccountNumber(e.target.value);
            setRecipientVerified(false);
            setRecipientName('');
          }}
          placeholder="Enter account number"
          className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-900 bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
        />
      </div>

      {!recipientVerified && recipientBankCode && recipientAccountNumber && (
        <button
          onClick={verifyRecipient}
          disabled={verifyingRecipient}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
        >
          {verifyingRecipient ? (
            <><Loader className="w-4 h-4 animate-spin" />Verifying account...</>
          ) : (
            <><Shield className="w-4 h-4" />Verify Recipient Account</>
          )}
        </button>
      )}

      {recipientVerified && recipientName && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <div className="font-bold text-emerald-800 text-sm">Recipient Verified</div>
            <div className="text-emerald-700 font-semibold mt-0.5">{recipientName}</div>
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button
          onClick={() => setCurrentStep(2)}
          className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-4 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={() => setCurrentStep(4)}
          disabled={!canProceedFromStep3()}
          className="flex-1 bg-violet-600 hover:bg-violet-700 text-white py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-lg shadow-violet-200/60"
        >
          Review
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // ==========================================
  // RENDER: STEP 4 - REVIEW
  // ==========================================
  const renderStep4 = () => {
    const selectedWalletDetails = getSelectedWalletDetails();
    const selectedBankDetails = getSelectedBankDetails();

    return (
      <div className="space-y-5">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-1">Review &amp; Confirm</h2>
          <p className="text-slate-500 text-sm">Check all details before sending</p>
        </div>

        {/* Transfer summary */}
        <div className="bg-gradient-to-br from-violet-600 to-indigo-600 rounded-2xl p-6 text-white shadow-lg shadow-violet-300/40">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-violet-300 text-xs font-bold uppercase tracking-wider mb-1">You send</div>
              <div className="text-4xl font-extrabold">{amount} <span className="text-2xl text-violet-300">{fromCurrency}</span></div>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
              <ArrowRight className="w-6 h-6" />
            </div>
            <div className="text-right">
              <div className="text-violet-300 text-xs font-bold uppercase tracking-wider mb-1">They receive</div>
              <div className="text-4xl font-extrabold">{quote?.convertedAmount?.toLocaleString()} <span className="text-2xl text-violet-300">{toCurrency}</span></div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 border-t border-violet-500/60 pt-4 text-center text-sm">
            <div>
              <div className="text-violet-300 text-xs mb-0.5">Rate</div>
              <div className="font-bold">1 {fromCurrency} = {quote?.exchangeRate}</div>
            </div>
            <div>
              <div className="text-violet-300 text-xs mb-0.5">Fee</div>
              <div className="font-bold">${quote?.fee}</div>
            </div>
            <div>
              <div className="text-violet-300 text-xs mb-0.5">Total</div>
              <div className="font-bold">${quote?.totalCost}</div>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-100">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Payment Method</span>
          </div>
          <div className="p-5 flex items-center gap-3">
            {selectedPaymentMethod === 'crypto' && selectedWalletDetails && (
              <>
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <Bitcoin className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <div className="font-semibold text-slate-800 text-sm">{selectedWalletDetails.name || 'Crypto Card'}</div>
                  <div className="text-xs text-slate-500">
                    {selectedWalletDetails.btc_balance || selectedWalletDetails.current_btc_balance} BTC available
                  </div>
                </div>
              </>
            )}
            {selectedPaymentMethod === 'bank' && selectedBankDetails && (
              <>
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Building className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-800 text-sm">{selectedBankDetails.bank_name}</div>
                  <div className="text-xs text-slate-500">Account ****{selectedBankDetails.account_number_last4}</div>
                </div>
              </>
            )}
            {selectedPaymentMethod === 'card' && (
              <>
                <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-800 text-sm">Debit / Credit Card</div>
                  <div className="text-xs text-slate-500">Secured by Stripe</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Recipient Details */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-100">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Recipient</span>
          </div>
          <div className="p-5 space-y-3 text-sm">
            {[
              { label: 'Name',    value: recipientName },
              { label: 'Bank',    value: banks.find(b => b.code === recipientBankCode)?.name },
              { label: 'Account', value: <span className="font-mono">{recipientAccountNumber}</span> },
              { label: 'Country', value: countries.find(c => c.code === recipientCountry)?.name },
            ].map(row => (
              <div key={row.label} className="flex justify-between items-center border-b border-slate-50 pb-2 last:border-0 last:pb-0">
                <span className="text-slate-500">{row.label}</span>
                <span className="text-slate-900 font-semibold">{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Security Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-blue-700 text-xs leading-relaxed">
            Your payment is encrypted with 256-bit bank-level security. Funds transfer directly to the recipient's bank account and can be tracked in your Transactions page.
          </p>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            onClick={() => setCurrentStep(3)}
            className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-4 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={handleSendMoney}
            disabled={loading}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-emerald-200/60"
          >
            {loading ? (
              <><Loader className="w-4 h-4 animate-spin" />Processing...</>
            ) : (
              <><Send className="w-4 h-4" />Send ${amount}</>
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==========================================
  // MAIN RENDER
  // ==========================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">

      {/* ── Full App.js Purple Header ── */}
      <header className="bg-purple-900/50 backdrop-blur-sm border-b border-purple-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src="/zanors-logo-full.png" alt="Zanors Logo"
                className="h-16 md:h-20 mix-blend-normal" style={{ backgroundColor:'transparent' }}/>
            </Link>
            <div className="flex items-center space-x-3">
              <Link to="/shop" className="text-orange-400 hover:text-orange-300 font-semibold text-sm md:text-base transition-colors flex items-center gap-2">
                <ShoppingCart size={18}/> Shop Cards
              </Link>
              <button onClick={() => setShowSignupModal(true)}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors">
                Sign Up
              </button>
              <button onClick={() => setShowLoginModal(true)}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors">
                Login
              </button>
              <button onClick={() => setShowActivateCardModal(true)}
                className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors">
                Activate Card
              </button>
              <button onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:bg-purple-700 rounded-lg transition-colors">
                {isMenuOpen ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="bg-purple-800 border-t border-purple-700 max-h-96 overflow-y-auto">
            <div className="container mx-auto px-4 py-4">
              <Link to="/shop" onClick={() => setIsMenuOpen(false)}
                style={{ background:'linear-gradient(90deg,rgba(212,168,50,0.2) 0%,rgba(245,166,35,0.2) 100%)', borderLeft:'3px solid #D4A832' }}
                className="w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 hover:bg-purple-700/50 mb-4">
                <ShoppingCart size={20} className="text-orange-500"/>
                <div>
                  <div className="font-semibold text-orange-400">Shop Crypto Cards</div>
                  <div className="text-xs text-purple-300">$50, $100, $200, $500 • Instant delivery</div>
                </div>
              </Link>
              <div className="border-t border-purple-600 my-2"></div>
              {Object.entries(menuItems).map(([category, items]) => (
                <div key={category} className="mb-2">
                  <button onClick={() => toggleSubmenu(category)}
                    className="w-full flex items-center justify-between p-3 hover:bg-purple-700 rounded-lg transition-colors">
                    <span className="font-semibold text-lg">{category}</span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${expandedMenu===category?'rotate-180':''}`}/>
                  </button>
                  {expandedMenu===category && (
                    <div className="ml-4 mt-2 space-y-1 bg-purple-900/50 rounded-lg p-2">
                      {items.map(item => (
                        <Link key={item.name} to={item.path}
                          className="block p-3 hover:bg-purple-700 rounded-lg text-gray-200 hover:text-white"
                          onClick={() => setIsMenuOpen(false)}>
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* ── Page Content ── */}
      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-2xl mb-5 flex items-start gap-3 shadow-sm">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" />
            <div className="flex-1 text-sm font-medium">{error}</div>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 p-4 rounded-2xl mb-5 flex items-start gap-3 shadow-sm">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-500" />
            <div className="flex-1 text-sm font-bold">{success}</div>
            <button onClick={() => setSuccess('')} className="text-emerald-400 hover:text-emerald-600 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Progress Steps */}
        {renderProgressSteps()}

        {/* Step Content */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-7">
          {currentStep === STEPS.AMOUNT && renderStep1()}
          {currentStep === STEPS.PAYMENT && renderStep2()}
          {currentStep === STEPS.RECIPIENT && renderStep3()}
          {currentStep === STEPS.REVIEW && renderStep4()}
        </div>

        {/* Trust badges */}
        <div className="mt-6 flex items-center justify-center gap-6 text-xs text-purple-300 flex-wrap">
          <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5"/> 256-bit encryption</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5"/> Real-time FX rates</span>
          <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5"/> 120+ countries</span>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="bg-black bg-opacity-50 mt-16 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {Object.entries(menuItems).map(([section, links]) => (
              <div key={section}>
                <h4 className="font-bold text-lg mb-4 text-white">{section}</h4>
                <ul className="space-y-2 text-purple-300">
                  {links.map(item => (
                    <li key={item.name}>
                      <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-purple-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <div className="w-14 h-14 rounded-full flex items-center justify-center p-2 border border-purple-600 shadow-lg flex-shrink-0">
                  <img src="/zanors-icon.png" alt="Zanors Icon" className="w-full h-full object-contain"/>
                </div>
                <div>
                  <span className="text-white font-bold">Zanors</span>
                  <p className="text-xs text-purple-400">Zanors is a financial technology platform. Zanors is not an FDIC-insured Bank or depositary</p>
                  <p className="text-xs text-purple-400">taking institution. In the USA, banking services are provided by BankProv, Member FDIC.</p>
                  <p className="text-xs text-purple-400">Crypto assets and non-bank digital wallet are not insured by the FDIC, are not deposits, and may lose value.</p>
                </div>
              </div>
              <div className="text-center md:text-right flex-shrink-0">
                <p className="text-purple-300 text-sm">© 2026 Zanors Inc. All rights reserved.</p>
                <p className="text-purple-400 text-xs mt-1">Licensed by FinCEN · Regulated by NMLS · Member FDIC</p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* ── Modals ── */}
      <SignupModal
        showSignupModal={showSignupModal}
        setShowSignupModal={setShowSignupModal}
        signupData={signupData}
        setSignupData={setSignupData}
        countries={signupCountries}
        setShowLandingPage={() => {}}
      />
      <LoginModal
        showLoginModal={showLoginModal}
        setShowLoginModal={setShowLoginModal}
        loginStep={loginStep}
        setLoginStep={setLoginStep}
        loginData={loginData}
        setLoginData={setLoginData}
        generateAuthCode={() => {}}
        setShowLandingPage={() => {}}
      />
      <ActivateCardModal
        showActivateCardModal={showActivateCardModal}
        setShowActivateCardModal={setShowActivateCardModal}
        activateData={activateData}
        setActivateData={setActivateData}
        onSuccess={() => {}}
      />
    </div>
  );
};

export default SendMoney;